import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { User, Shield, Wrench, MapPin, LogOut, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/_authenticated/konto")({
  head: () => ({ meta: [{ title: "Mitt konto – AK Husbil & Fritid" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: KontoLayout,
});

const links: Array<{ to: string; label: string; icon: typeof User; exact?: boolean }> = [
  { to: "/konto", label: "Översikt", icon: LayoutDashboard, exact: true },
  { to: "/konto/profil", label: "Profil", icon: User },
  { to: "/konto/mina-forfragningar", label: "Mina reservdelar", icon: Wrench },
  { to: "/konto/mina-tips", label: "Mina campingtips", icon: MapPin },
  { to: "/konto/sakerhet", label: "Säkerhet", icon: Shield },
];

function KontoLayout() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    toast.success("Utloggad");
    navigate({ to: "/", replace: true });
  }

  return (
    <section className="bg-warm">
      <div className="container-edge py-12 md:py-16">
        <p className="eyebrow">Ditt konto</p>
        <h1 className="mt-3 font-display text-4xl md:text-6xl">
          Mitt <span className="font-italic-display text-accent">konto</span>.
        </h1>

        <div className="mt-10 grid gap-8 md:grid-cols-[240px_1fr]">
          <aside className="md:sticky md:top-24 md:self-start">
            <nav className="flex flex-wrap gap-1 md:flex-col">
              {links.map((l) => {
                const Icon = l.icon;
                return (
                  <Link
                    key={l.to}
                    to={l.to as any}
                    activeOptions={{ exact: l.exact ?? false }}
                    className="inline-flex items-center gap-2.5 rounded-full px-4 py-2.5 text-sm font-medium text-foreground/80 transition hover:bg-secondary"
                    activeProps={{ className: "inline-flex items-center gap-2.5 rounded-full px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground" }}
                  >
                    <Icon size={15} /> {l.label}
                  </Link>
                );
              })}
              <button
                onClick={signOut}
                className="mt-2 inline-flex items-center gap-2.5 rounded-full px-4 py-2.5 text-left text-sm font-medium text-foreground/70 transition hover:bg-secondary hover:text-foreground"
              >
                <LogOut size={15} /> Logga ut
              </button>
            </nav>
          </aside>

          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </section>
  );
}
